import React, { useState, useEffect, useRef } from "react";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import Attachments from "../Shared/Attachments.js";
import CommentsList from "../Shared/CommentsList/CommentsList.js";
import Audit from "../Shared/Audit.js";
import EntityAssociation from "../Shared/EntityAssociation.js";
import * as FieldTypes from "../Shared/FieldTypes/FieldTypes.js";
import { ComplianceStatuses } from "../Shared/Constants.js";
import * as Shared from "./Shared.js";
import SectionTree from "./SectionTree.js";
import SectionEditor from "./SectionEditor.js";
import SectionVersionHistory from "./SectionVersionHistory.js";
import ApprovalPane from "./ApprovalPane.js";
import EmptyState from "./Empty.js";
import "./App.css";

const ENTITY_TYPE = "compliance-technical-documents";
const API = "/umbraco/api/TechnicalDocumentation";

const TABS = [
    { key: "details", title: "eTD Details", icon: "fas fa-info-circle" },
    { key: "structure", title: "Structure & Content", icon: "fas fa-sitemap" },
    { key: "reviewers", title: "Reviewers", icon: "fas fa-user-check" },
    { key: "linkedto", title: "Linked Items", icon: "fas fa-project-diagram" },
    { key: "attachments", title: "Attachments", icon: "fas fa-paperclip" },
    { key: "comments", title: "Comments", icon: "far fa-comment-alt" },
    { key: "history", title: "History", icon: "fas fa-history" },
];

const SV_APPROVED = 1;

const ItemDetailsPane = ({ globalContext }) => {
    const AppContext = globalContext;
    const techDoc = AppContext.currentItem;
    const techDocId = techDoc?.Id;

    const [paneActive, setPaneActive] = useState(AppContext.isPaneOpen);
    const [openEffect, setOpenEffect] = useState(false);
    const [visibleTab, setVisibleTab] = useState("structure");

    const [users, setUsers] = useState(AppContext.users || []);
    const currentUserId = AppContext.currentUserId;

    const [versions, setVersions] = useState([]);
    const [isLoadingVersions, setIsLoadingVersions] = useState(false);
    const [selectedVersionId, setSelectedVersionId] = useState(null);

    const [sections, setSections] = useState([]);
    const [sectionVersions, setSectionVersions] = useState([]);   // current _SectionVersion per section
    const [isLoadingSections, setIsLoadingSections] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [reviewers, setReviewers] = useState([]);   // roster for selectedVersionId

    const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
    const [docJurisdictions, setDocJurisdictions] = useState([]);   // this document's own jurisdictions
    const [sectionLinks, setSectionLinks] = useState([]);   // whole-document section-to-section links

    const [isDirty, setIsDirty] = useState(false);
    const [busyAction, setBusyAction] = useState(null);
    const [popup, setPopup] = useState(null);            // "version" | "editDoc" | "submission" | "saveTemplate" | "addSection" | null
    const [templateError, setTemplateError] = useState(null);
    const [addSectionParentId, setAddSectionParentId] = useState(null);
    const [addSectionError, setAddSectionError] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);  // { sectionIdentifier, title } | null
    const [showReview, setShowReview] = useState(false);
    const [kebabOpen, setKebabOpen] = useState(false);
    const [error, setError] = useState(null);
    const [pendingNav, setPendingNav] = useState(null);   // () => void, held while the leave-guard is up
    const [pendingNavError, setPendingNavError] = useState(null);
    const [savingPendingNav, setSavingPendingNav] = useState(false);

    const kebabRef = useRef(null);
    const sectionEditorRef = useRef(null);

    // ---- pane open / close animation ----
    useEffect(() => {
        if (AppContext.isPaneOpen) {
            setPaneActive(true);
            setTimeout(() => setOpenEffect(true), 60);
        } else {
            setOpenEffect(false);
            setTimeout(() => setPaneActive(false), 300);
        }
    }, [AppContext.isPaneOpen]);

    useEffect(() => {
        if (paneActive) {
            setVisibleTab("structure");
            setActiveSectionId(null);
            setIsDirty(false);
            setError(null);
        }
    }, [paneActive, techDocId]);

    useEffect(() => {
        const onClick = (e) => {
            if (kebabRef.current && !kebabRef.current.contains(e.target)) setKebabOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // ---- data loads ----
    useEffect(() => {
        if (!users.length) {
            fetch("/umbraco/api/TaskManagerApi/Users").then((r) => r.json())
                .then((d) => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
        }
        fetch(`${API}/Jurisdictions/`).then((r) => r.json())
            .then((d) => setJurisdictionOptions(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);

    useEffect(() => { if (techDocId) { loadVersions(); loadDocJurisdictions(); loadSectionLinks(); } }, [techDocId]);

    const loadDocJurisdictions = () => {
        fetch(`${API}/DocumentJurisdictions/?techDocId=${techDocId}`)
            .then((r) => r.json())
            .then((d) => setDocJurisdictions(Array.isArray(d) ? d : []))
            .catch(() => setDocJurisdictions([]));
    };

    const loadSectionLinks = () => {
        fetch(`${API}/SectionLinks/?techDocId=${techDocId}`)
            .then((r) => r.json())
            .then((d) => setSectionLinks(Array.isArray(d) ? d : []))
            .catch(() => setSectionLinks([]));
    };

    const addSectionLink = (sourceSectionIdentifier, targetSectionIdentifier, note) =>
        fetch(`${API}/SectionLinks/?techDocId=${techDocId}&sourceSectionIdentifier=${sourceSectionIdentifier}&targetSectionIdentifier=${targetSectionIdentifier}&note=${encodeURIComponent(note || "")}`, { method: "POST" })
            .then((r) => { if (!r.ok) throw new Error("link"); return r.json(); })
            .then((d) => { loadSectionLinks(); return d; });

    const removeSectionLink = (linkId) =>
        fetch(`${API}/SectionLinks/?techDocId=${techDocId}&linkId=${linkId}`, { method: "DELETE" })
            .then((r) => { if (!r.ok) throw new Error("unlink"); return r.json(); })
            .then((d) => { loadSectionLinks(); return d; });

    const loadVersions = () => {
        setIsLoadingVersions(true);
        fetch(`${API}/Versions/?techDocId=${techDocId}`)
            .then((r) => r.json())
            .then((data) => {
                const list = (Array.isArray(data) ? data : []).sort((a, b) => b.Id - a.Id);
                setVersions(list);
                setSelectedVersionId((prev) => (prev && list.find((v) => v.Id === prev)) ? prev : (list[0]?.Id ?? null));
            })
            .catch(() => setError("Could not load versions."))
            .finally(() => setIsLoadingVersions(false));
    };

    useEffect(() => {
        if (selectedVersionId) { loadSections(selectedVersionId); loadSectionVersions(selectedVersionId); loadReviewers(selectedVersionId); }
        else { setSections([]); setSectionVersions([]); setReviewers([]); }
    }, [selectedVersionId]);

    const loadSections = (versionId) => {
        setIsLoadingSections(true);
        fetch(`${API}/Sections/?techDocId=${techDocId}&versionId=${versionId}`)
            .then((r) => r.json())
            .then((data) => setSections(Array.isArray(data) ? data : []))
            .catch(() => setError("Could not load sections."))
            .finally(() => setIsLoadingSections(false));
    };

    const loadSectionVersions = (versionId) => {
        fetch(`${API}/SectionVersionsCurrent/?techDocId=${techDocId}&versionId=${versionId}`)
            .then((r) => r.json())
            .then((data) => setSectionVersions(Array.isArray(data) ? data : []))
            .catch(() => setSectionVersions([]));
    };

    const loadReviewers = (versionId) => {
        fetch(`${API}/Reviewers/?techDocId=${techDocId}&versionId=${versionId}`)
            .then((r) => r.json())
            .then((data) => setReviewers(Array.isArray(data) ? data : []))
            .catch(() => setReviewers([]));
    };

    // ---- derived ----
    const latestVersion = versions[0] || null;
    const currentVersion = versions.find((v) => v.Id === selectedVersionId) || null;
    const isLatest = latestVersion && selectedVersionId === latestVersion.Id;
    const myReviewerRow = reviewers.find((r) => r.MemberId == currentUserId);
    const isReviewer = currentVersion && !!myReviewerRow;
    // Read-level members can never edit, regardless of whether they authored this version --
    // authorship is not an authorization override.
    const canEdit = !!(isLatest && currentVersion && !currentVersion.Published && AppContext.userRole === "Admin");

    const numbers = Shared.buildSectionNumbers(sections);
    const svByIdentifier = {};
    sectionVersions.forEach((sv) => { svByIdentifier[sv.SectionIdentifier] = sv; });

    // sectionId -> "1.2 Section Title", for labeling comments/attachments outside their section.
    // Keyed by both this version's row id (attachments) and the canonical id (comments key against
    // that instead, so a comment still resolves after its section is cloned into a new version).
    const sectionLabels = {};
    sections.forEach((s) => {
        const label = `${numbers[s.Id] ? numbers[s.Id] + " " : ""}${s.Title}`;
        sectionLabels[s.Id] = label;
        if (s.CanonicalSectionId) sectionLabels[s.CanonicalSectionId] = label;
    });

    const total = sections.length;
    const approvedCount = sections.filter((s) => svByIdentifier[s.SectionIdentifier]?.State === SV_APPROVED).length;
    const pendingCount = total - approvedCount;
    const allApproved = total > 0 && approvedCount === total;

    const activeSection = sections.find((s) => s.Id === activeSectionId) || null;
    const activeSV = activeSection ? svByIdentifier[activeSection.SectionIdentifier] : null;

    // ---- actions ----
    // Any navigation away from a dirty section goes through this instead of firing immediately, so
    // the leave-guard modal (Cancel / Discard / Save) can decide whether it actually happens.
    const guardedNav = (action) => {
        if (isDirty) { setPendingNavError(null); setPendingNav(() => action); return; }
        action();
    };

    // eTD Details lives inside Structure & Content: it's that tab with no section selected.
    const isTabActive = (key) =>
        key === "details" ? visibleTab === "structure" && !activeSectionId
        : key === "structure" ? visibleTab === "structure" && !!activeSectionId
        : visibleTab === key;

    const selectSection = (s) => guardedNav(() => { setIsDirty(false); setActiveSectionId(s.Id); });


    const onSectionSaved = () => {
        loadSections(selectedVersionId);
        loadSectionVersions(selectedVersionId);
        AppContext.refreshData && AppContext.refreshData();
    };

    const createVersion = () => {
        setBusyAction("version");
        fetch(`${API}/Versions/?techDocId=${techDocId}`, { method: "POST" })
            .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
            .then(() => { setPopup(null); loadVersions(); })
            .catch(() => setError("Could not start a new version."))
            .finally(() => setBusyAction(null));
    };

    const moveSection = (section, dir) => {
        const siblings = sections
            .filter((s) => (s.ParentId ?? null) === (section.ParentId ?? null))
            .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
        const idx = siblings.findIndex((s) => s.Id === section.Id);
        const target = siblings[idx + dir];
        if (!target) return;
        fetch(`${API}/Sections/?techDocId=${techDocId}&sectionOrigId=${section.Id}&sectionDestId=${target.Id}`, { method: "PATCH" })
            .then((r) => { if (!r.ok) throw new Error(); return r.text(); })
            .then(() => loadSections(selectedVersionId))
            .catch(() => setError("Could not move the section."));
    };

    const openAddSection = (parentId) => {
        setAddSectionParentId(parentId);
        setAddSectionError(null);
        setPopup("addSection");
    };

    const submitAddSection = (title) => {
        if (!title.trim()) return;
        setBusyAction("addSection");
        setAddSectionError(null);
        const qs = new URLSearchParams({ techDocId, title: title.trim(), description: "" });
        if (addSectionParentId != null) qs.set("parentId", addSectionParentId);
        fetch(`${API}/Sections/?${qs.toString()}`, { method: "POST" })
            .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
            .then((created) => { onSectionSaved(); if (created?.Id) setActiveSectionId(created.Id); setPopup(null); })
            .catch(() => setAddSectionError("Could not add the section."))
            .finally(() => setBusyAction(null));
    };

    const deleteSection = (section) => {
        if (!window.confirm(`Delete "${section.Title}"? It will be removed from this draft version.`)) return;
        fetch(`${API}/Sections/?techDocId=${techDocId}&sectionId=${section.Id}`, { method: "DELETE" })
            .then((r) => { if (!r.ok) throw new Error(); return r.text(); })
            .then(() => { if (activeSectionId === section.Id) setActiveSectionId(null); onSectionSaved(); })
            .catch(() => setError("Could not delete the section."));
    };

    const clearNeedsReview = (sectionId) => {
        fetch(`${API}/SectionNeedsReview/?techDocId=${techDocId}&sectionId=${sectionId}`, { method: "DELETE" })
            .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
            .then(() => loadSections(selectedVersionId))
            .catch(() => setError("Could not dismiss the alert."));
    };

    // Full snapshot + overrides: the API replaces Entities/JurisdictionIds wholesale, so a
    // partial body would silently clear whichever isn't included.
    const saveDocument = (overrides = {}) => {
        const body = {
            Id: techDocId,
            Name: techDoc?.Name,
            Status: techDoc?.Status,
            Format: techDoc?.Format,
            Entities: AppContext.linkedObjectsList || [],
            JurisdictionIds: docJurisdictions.map((j) => j.Id),
            CustomFieldsAnswers: techDoc?.CustomFieldsAnswers || [],
            ...overrides,
        };
        return fetch("/umbraco/api/Compliance/TechnicalDocumentation", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        })
            .then((r) => { if (!r.ok) throw new Error("save"); return r.json(); })
            .then((result) => {
                if (result?.Id) {
                    AppContext.setCurrentItem && AppContext.setCurrentItem(result);
                    if (Array.isArray(AppContext.data) && AppContext.setData)
                        AppContext.setData(AppContext.data.map((item) => (item.Id === result.Id ? result : item)));
                }
                loadDocJurisdictions();
                return result;
            });
    };

    // One-time export -- not persisted anywhere on the document.
    const sendToSubmission = (submissionId) =>
        fetch(`${API}/SendToSubmission/?techDocId=${techDocId}&submissionId=${submissionId}`, { method: "POST" })
            .then((r) => { if (!r.ok) throw new Error("send"); return r.json(); })
            .then((d) => { if (d?.success === false) throw new Error("send"); return d; });

    // Saves the currently-viewed version's section titles/nesting (not content) as a reusable
    // template -- the same template list used when creating a new Technical Documentation.
    const saveAsTemplate = (name) => {
        if (!name.trim() || !currentVersion || !currentVersion.Published) return;
        setBusyAction("saveTemplate");
        setTemplateError(null);
        fetch(`${API}/Templates/?techDocId=${techDocId}&versionId=${currentVersion.Id}&name=${encodeURIComponent(name.trim())}`, { method: "POST" })
            .then((r) => { if (!r.ok) throw new Error("template"); return r.json(); })
            .then(() => setPopup(null))
            .catch(() => setTemplateError("Could not save this structure as a template."))
            .finally(() => setBusyAction(null));
    };

    const closePane = () => guardedNav(() => {
        setIsDirty(false);
        AppContext.setCurrentItem && AppContext.setCurrentItem(null);
        AppContext.closePane();
    });

    const cancelPendingNav = () => { setPendingNav(null); setPendingNavError(null); };

    const discardPendingNav = () => {
        const action = pendingNav;
        setPendingNav(null);
        setPendingNavError(null);
        action && action();
    };

    const saveAndRunPendingNav = () => {
        const action = pendingNav;
        if (!sectionEditorRef.current?.save) { setPendingNav(null); action && action(); return; }
        setSavingPendingNav(true);
        sectionEditorRef.current.save()
            .then(() => { setPendingNav(null); setPendingNavError(null); action && action(); })
            .catch(() => setPendingNavError("Could not save this section. Try again, or discard your changes."))
            .finally(() => setSavingPendingNav(false));
    };

    if (!paneActive) return null;
    const loadingDoc = techDoc == null || AppContext.isLoadingItemDetails;

    return (
        <div
            className={`slide-pane-full-width ${openEffect ? "open" : ""}`}
            style={{ height: "100vh", overflow: "hidden", background: "#f7fafc", display: "flex", flexDirection: "column" }}
        >
            {/* ---- header ---- */}
            <div className="td-pane-header">
                <div className="td-pane-header-left">
                    <i className="fas fa-book" style={{ color: "#2b6cb0" }} />
                    <span className="td-pane-title" title={techDoc?.Name}>
                        {loadingDoc ? "…" : (techDoc?.Name || "Technical Documentation")}
                    </span>
                    {currentVersion && (
                        <>
                            <select
                                className="td-version-select"
                                value={selectedVersionId || ""}
                                onChange={(e) => {
                                    const id = Number(e.target.value);
                                    guardedNav(() => { setIsDirty(false); setActiveSectionId(null); setSelectedVersionId(id); });
                                }}
                                title="Switch version"
                            >
                                {versions.map((v) => (
                                    <option key={v.Id} value={v.Id}>v{v.VersionNumber} {v.Published ? "· Approved" : "· Draft"}</option>
                                ))}
                            </select>
                            <Shared.VersionStatePill published={currentVersion.Published} />
                        </>
                    )}
                    {isLatest && !currentVersion?.Published && pendingCount > 0 && (
                        <span className="td-status-pill" style={{ background: "#fef4dc", color: "#8a6a12" }}>
                            {pendingCount} section{pendingCount > 1 ? "s" : ""} not yet approved
                        </span>
                    )}
                </div>

                <div className="td-tabs">
                    {TABS.map((t) => (
                        <a
                            key={t.key}
                            className={`td-tab ${isTabActive(t.key) ? "active" : ""}`}
                            onClick={() => guardedNav(() => {
                                setIsDirty(false);
                                setVisibleTab(t.key === "details" ? "structure" : t.key);
                                if (t.key === "details") setActiveSectionId(null);
                            })}
                        >
                            <i className={t.icon} />
                            {t.title}
                        </a>
                    ))}
                </div>

                <div className="td-pane-header-right">
                    {isLatest && currentVersion?.Published && AppContext.userRole === "Admin" && (
                        <button className="td-btn td-btn-primary" onClick={() => setPopup("version")}>
                            <i className="fas fa-plus-circle" /> Start new version
                        </button>
                    )}
                    {isReviewer && isLatest && !currentVersion?.Published && (
                        <button
                            className={`td-btn ${allApproved ? "td-btn-approve" : "td-btn-primary"}`}
                            onClick={() => setShowReview(true)}
                        >
                            <i className={allApproved ? "fas fa-signature" : "far fa-check-circle"} />
                            {allApproved ? " Approve & sign document" : " Review sections"}
                        </button>
                    )}
                    {(AppContext.userRole === "Admin" || AppContext.rimsUserRole === "Admin") && (
                        <span className="td-kebab" ref={kebabRef} onClick={() => setKebabOpen(!kebabOpen)} style={{ position: "relative" }}>
                            <i className="fas fa-ellipsis-v" />
                            {kebabOpen && (
                                <div className="td-kebab-menu" style={{ right: 0, top: 26 }} onClick={(e) => e.stopPropagation()}>
                                    {AppContext.rimsUserRole === "Admin" && versions.length > 0 && (
                                        <div
                                            className="td-kebab-menu-item"
                                            onClick={() => { setKebabOpen(false); setPopup("submission"); }}
                                        >
                                            <i className="fas fa-share-square" /> Send to submission
                                        </div>
                                    )}
                                    {AppContext.userRole === "Admin" && (<>
                                        <div
                                            className="td-kebab-menu-item"
                                            onClick={() => { setKebabOpen(false); setTemplateError(null); setPopup("saveTemplate"); }}
                                        >
                                            <i className="fas fa-layer-group" /> Save as template
                                        </div>
                                        <div
                                            className="td-kebab-menu-item danger"
                                            onClick={() => {
                                                if (AppContext.isDeletingItem) return;
                                                // Stays open (rather than closing immediately) so the spinner below is
                                                // actually visible while the delete is in flight; the pane itself
                                                // closes on success, or this reverts to normal on failure.
                                                AppContext.deleteCurrentItem && AppContext.deleteCurrentItem({ callBack: () => {} });
                                            }}
                                        >
                                            {AppContext.isDeletingItem ? <LoadingSpinnerImage /> : <><i className="far fa-trash-alt" /> Delete document</>}
                                        </div>
                                    </>)}
                                </div>
                            )}
                        </span>
                    )}
                    <span className="td-close-x" onClick={closePane}><i className="fas fa-times" /></span>
                </div>
            </div>

            {error && (
                <div className="td-err" style={{ padding: "8px 28px", background: "#fde8ec", flex: "none" }}>
                    {error} <span style={{ cursor: "pointer", marginLeft: 8, textDecoration: "underline" }} onClick={() => setError(null)}>dismiss</span>
                </div>
            )}

            {/* ---- body ---- */}
            <div style={{ flex: 1, minHeight: 0, display: "flex", padding: "12px 16px", overflow: "hidden" }}>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {loadingDoc ? (
                        <div className="td-center-loading"><LoadingSpinnerImage /></div>
                    ) : visibleTab === "reviewers" ? (
                        <ReviewersTab techDocId={techDocId} versionId={selectedVersionId} users={users}
                            reviewers={reviewers} currentVersion={currentVersion} currentUserId={currentUserId}
                            canManage={AppContext.userRole === "Admin" && isLatest && !currentVersion?.Published}
                            onChanged={() => { loadReviewers(selectedVersionId); loadVersions(); }} />
                    ) : visibleTab === "structure" ? (
                        <StructureTab
                            {...{
                                AppContext, techDoc, techDocId, versions, isLoadingVersions,
                                sectionEditorRef,
                                sections, sectionVersions, svByIdentifier, isLoadingSections, numbers,
                                activeSection, activeSV, activeSectionId, selectSection, canEdit,
                                moveSection, addSection: openAddSection, deleteSection, clearNeedsReview,
                                jurisdictionOptions, docJurisdictions, users, currentUserId, reviewers, onSectionSaved, setIsDirty,
                                isEditingDoc: popup === "editDoc", onEditDoc: () => setPopup("editDoc"),
                                sectionLinks, onAddLink: addSectionLink, onRemoveLink: removeSectionLink,
                                currentVersion, isLatest, onCreateVersion: () => setPopup("version"),
                                onOpenHistory: (s) => setHistoryFor({ sectionIdentifier: s.SectionIdentifier, title: s.Title }),
                            }}
                        />
                    ) : visibleTab === "attachments" ? (
                        <AttachmentsTab techDocId={techDocId} versionId={selectedVersionId} sectionLabels={sectionLabels} users={users} userRole={AppContext.userRole} />
                    ) : visibleTab === "comments" ? (
                        <div style={{ overflowY: "auto", paddingRight: 8 }}>
                            <CommentsList entityType={ENTITY_TYPE} entityId={techDocId} userList={users} currentUserId={currentUserId} sectionLookup={sectionLabels} />
                        </div>
                    ) : visibleTab === "linkedto" ? (
                        <LinkedItemsTab AppContext={AppContext} onSave={saveDocument} />
                    ) : (
                        <HistoryTab techDocId={techDocId} versions={versions} users={users} />
                    )}
                </div>
            </div>

            {/* ---- popups ---- */}
            {popup === "version" && (
                <ConfirmPopup
                    title="Start a new version"
                    body={`Start version ${(parseInt(latestVersion?.VersionNumber) + 1) || 1} of this Technical Documentation. The structure and content carry over from v${latestVersion?.VersionNumber}.`}
                    confirmLabel={`Start v${(parseInt(latestVersion?.VersionNumber) + 1) || 1}`}
                    busy={busyAction === "version"}
                    onConfirm={createVersion}
                    onClose={() => setPopup(null)}
                />
            )}
            {popup === "editDoc" && (
                <EditDocumentModal
                    techDoc={techDoc}
                    docJurisdictions={docJurisdictions}
                    jurisdictionOptions={jurisdictionOptions}
                    AppContext={AppContext}
                    onSave={(overrides) => saveDocument(overrides).then(() => setPopup(null))}
                    onClose={() => setPopup(null)}
                />
            )}
            {popup === "submission" && (
                <SubmissionPicker
                    onSend={sendToSubmission}
                    onClose={() => setPopup(null)}
                />
            )}
            {popup === "saveTemplate" && (
                <SaveAsTemplateModal
                    busy={busyAction === "saveTemplate"}
                    error={templateError}
                    isApproved={!!currentVersion?.Published}
                    onSave={saveAsTemplate}
                    onClose={() => setPopup(null)}
                />
            )}
            {popup === "addSection" && (
                <AddSectionModal
                    busy={busyAction === "addSection"}
                    error={addSectionError}
                    onSave={submitAddSection}
                    onClose={() => setPopup(null)}
                />
            )}
            {showReview && currentVersion && (
                <ApprovalPane
                    techDocId={techDocId}
                    versionId={currentVersion.Id}
                    versionNumber={currentVersion.VersionNumber}
                    documentName={techDoc?.Name}
                    signer={{
                        id: currentUserId,
                        name: myReviewerRow?.Name || Shared.userName(users, currentUserId),
                        role: myReviewerRow?.Role || "",
                        email: myReviewerRow?.Email || "",
                    }}
                    onClose={() => setShowReview(false)}
                    onChanged={() => { loadVersions(); loadSections(selectedVersionId); loadSectionVersions(selectedVersionId); }}
                />
            )}

            {historyFor && (
                <SectionVersionHistory
                    techDocId={techDocId}
                    techDocName={techDoc?.Name}
                    sectionIdentifier={historyFor.sectionIdentifier}
                    sectionTitle={historyFor.title}
                    versions={versions}
                    onClose={() => setHistoryFor(null)}
                />
            )}

            {pendingNav && (
                <UnsavedChangesModal
                    error={pendingNavError}
                    busy={savingPendingNav}
                    onCancel={cancelPendingNav}
                    onDiscard={discardPendingNav}
                    onSave={saveAndRunPendingNav}
                />
            )}
        </div>
    );
};


/* -------------------------------------------------------------------------- */

const StructureTab = ({
    AppContext, techDoc, techDocId, versions, isLoadingVersions, sectionEditorRef,
    sections, svByIdentifier, isLoadingSections, numbers, activeSection, activeSV, activeSectionId,
    selectSection, canEdit, moveSection, addSection, deleteSection, clearNeedsReview,
    jurisdictionOptions, docJurisdictions, users, currentUserId, reviewers, onSectionSaved, setIsDirty, currentVersion, isLatest,
    sectionLinks, onAddLink, onRemoveLink, isEditingDoc, onEditDoc,
    onCreateVersion, onOpenHistory,
}) => {
    if (isLoadingVersions) return <div className="td-center-loading"><LoadingSpinnerImage /></div>;
    if (!versions.length) return <EmptyState canEdit={canEdit} onStart={onCreateVersion} />;

    return (
        <>
            <div className="td-workspace" style={{ flex: 1, minHeight: 0 }}>
                {isLoadingSections ? (
                    <div className="td-center-loading"><LoadingSpinnerImage /></div>
                ) : (
                    <>
                        <SectionTree
                            sections={sections}
                            svByIdentifier={svByIdentifier}
                            numbers={numbers}
                            activeSectionId={activeSectionId}
                            onSelect={selectSection}
                            canEdit={canEdit}
                            onMove={moveSection}
                            onAddSection={addSection}
                            onDeleteSection={deleteSection}
                        />
                        {activeSection ? (
                            <SectionEditor
                                key={activeSectionId}
                                ref={sectionEditorRef}
                                techDocId={techDocId}
                                section={activeSection}
                                sectionVersion={activeSV}
                                number={numbers[activeSection.Id]}
                                allSections={sections}
                                numbers={numbers}
                                sectionLinks={sectionLinks}
                                onAddLink={onAddLink}
                                onRemoveLink={onRemoveLink}
                                canEdit={canEdit}
                                jurisdictionOptions={jurisdictionOptions}
                                users={users}
                                currentVersion={currentVersion}
                                currentUserId={currentUserId}
                                reviewers={reviewers}
                                onSaved={onSectionSaved}
                                onDirtyChange={setIsDirty}
                                onOpenHistory={() => onOpenHistory(activeSection)}
                                onClearNeedsReview={() => clearNeedsReview(activeSection.Id)}
                            />
                        ) : (
                            <DocumentOverviewPanel techDoc={techDoc} docJurisdictions={docJurisdictions} onEdit={onEditDoc}
                                AppContext={AppContext} isEditingDoc={isEditingDoc} currentVersion={currentVersion} />
                        )}
                    </>
                )}
            </div>
        </>
    );
};


const DocumentOverviewPanel = ({ techDoc, docJurisdictions, onEdit, AppContext, isEditingDoc, currentVersion }) => {
    const status = ComplianceStatuses.find((s) => s.id === techDoc?.Status);
    return (
        <div className="td-editor-panel">
            <div style={{ margin: "24px 26px" }}>
                <div className="td-row" style={{ justifyContent: "space-between", marginBottom: 18 }}>
                    <h3 style={{ fontSize: 17, color: "#1a202c", margin: 0 }}>{techDoc?.Name}</h3>
                    {AppContext.userRole === "Admin" && (
                        <span className="td-btn-outline" onClick={onEdit}>
                            <i className="fas fa-pen" /> Edit
                        </span>
                    )}
                </div>

                <div className="td-kv"><span>Name</span><span>{techDoc?.Name || "—"}</span></div>
                <div className="td-kv">
                    <span>Compliance status</span>
                    <span>
                        {status ? (
                            <span className="td-status-pill" style={{ background: status.bgColor, color: status.textColor }}>{status.name}</span>
                        ) : "—"}
                    </span>
                </div>
                <div className="td-kv">
                    <span>Format</span>
                    <span><FieldTypes.FieldType_TD_Format value={techDoc?.Format} /></span>
                </div>
                <div className="td-hr" />
                <div className="td-panel-h" style={{ marginTop: 0 }}>Jurisdictions</div>
                {docJurisdictions.length > 0 ? (
                    <div className="td-row" style={{ gap: 6, flexWrap: "wrap" }}>
                        {docJurisdictions.map((j) => <span key={j.Id} className="td-tag">{j.Name}</span>)}
                    </div>
                ) : (
                    <div className="td-muted" style={{ fontSize: 12.5 }}>None linked yet.</div>
                )}

                {/* Unmounted while the edit modal is open, so this never holds a second, separately-
                    stateful copy of the same field alongside the modal's -- it remounts fresh (and
                    correct) once the modal closes, the same way every other custom-fields screen in
                    the app only ever has one live copy of a field at a time. */}
                {!isEditingDoc && techDoc?.CustomFields?.length > 0 && (
                    <>
                        <div className="td-hr" />
                        <div className="td-panel-h">Custom fields</div>
                        <FieldTypes.FieldType_CustomUserFieldsView globalContext={AppContext} isEditing={false} />
                    </>
                )}
            </div>
        </div>
    );
};


// RIMS (Products + Country Registrations), Systematic Reviews and Compliance Manager, as in Standards / Regulations.
const LINKABLE_ENTITY_TYPES = [
    "product-skus", "product-family", "product-full-portfolio",
    "countryregistration-registration", "countryregistration-certificate",
    "countryregistration-operator", "countryregistration-country",
    "systematic-review-regulatory", "systematic-review-clinical", "systematic-review-marketpms",
    "compliance-regulatorydocuments", "compliance-standards", "compliance-qms", "compliance-technical-documents",
];

const LinkedItemsTab = ({ AppContext, onSave }) => {
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState(null);

    const save = () => {
        setSaving(true);
        setErr(null);
        setSaved(false);
        onSave()
            .then(() => { setSaved(true); setTimeout(() => setSaved(false), 3000); })
            .catch(() => setErr("Could not save the linked items."))
            .finally(() => setSaving(false));
    };

    return (
        <div style={{ overflowY: "auto", paddingRight: 8 }}>
            <EntityAssociation context={AppContext} entityTypeFilters={LINKABLE_ENTITY_TYPES} />
            <div className="td-row" style={{ justifyContent: "flex-end", gap: 12, margin: "12px 24px 16px 0" }}>
                {saved && <span className="td-receipt" style={{ padding: "6px 12px" }}><i className="fas fa-check-circle" /> Saved successfully.</span>}
                {err && <span className="td-err" style={{ marginTop: 0 }}>{err}</span>}
                <button className="td-btn td-btn-primary" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
};


const AttachmentsTab = ({ techDocId, versionId, sectionLabels, users, userRole }) => {
    const [sectionFiles, setSectionFiles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!versionId) { setSectionFiles([]); setLoading(false); return; }
        setLoading(true);
        fetch(`/umbraco/api/TechnicalDocumentation/SectionFiles/?techDocId=${techDocId}&versionId=${versionId}`)
            .then((r) => r.json())
            .then((d) => setSectionFiles(Array.isArray(d) ? d : []))
            .catch(() => setSectionFiles([]))
            .finally(() => setLoading(false));
    }, [techDocId, versionId]);

    return (
        <div style={{ overflowY: "auto", paddingRight: 8 }}>
            <Attachments EntityType={ENTITY_TYPE} EntityId={String(techDocId)} users={users} userRole={userRole} />

            <div className="td-hr" />
            <div className="td-panel-h">Section attachments</div>
            {loading ? (
                <div className="td-center-loading"><LoadingSpinnerImage /></div>
            ) : sectionFiles.length === 0 ? (
                <div className="td-muted" style={{ fontSize: 12.5 }}>None.</div>
            ) : (
                sectionFiles.map((f) => (
                    <div key={f.Id} className="td-kv">
                        <span><i className="far fa-file" style={{ color: "#2b6cb0", marginRight: 6 }} />{f.FileName}</span>
                        <span className="td-muted">
                            {sectionLabels[f.SectionId] || "—"}{f.FileSize ? ` · ${Shared.fileSize(f.FileSize)}` : ""}
                        </span>
                    </div>
                ))
            )}
        </div>
    );
};


const ReviewersTab = ({ techDocId, versionId, users, reviewers, currentVersion, currentUserId, canManage, onChanged }) => {
    const [busy, setBusy] = useState(null);       // memberId being added/removed
    const [adding, setAdding] = useState(false);
    const [editingAuthor, setEditingAuthor] = useState(false);
    const [savingAuthor, setSavingAuthor] = useState(false);
    const author = users.find((u) => u.Id == currentVersion?.Author);

    const availableUsers = users.filter((u) => !reviewers.find((r) => r.MemberId == u.Id));

    const changeAuthor = (authorId) => {
        if (!authorId) { setEditingAuthor(false); return; }
        setSavingAuthor(true);
        fetch(`/umbraco/api/TechnicalDocumentation/VersionAuthor/?techDocId=${techDocId}&authorId=${authorId}`, { method: "PATCH" })
            .then(() => onChanged && onChanged())
            .finally(() => { setSavingAuthor(false); setEditingAuthor(false); });
    };

    const addReviewer = (memberId) => {
        if (!memberId) return;
        setBusy(memberId);
        fetch(`/umbraco/api/TechnicalDocumentation/Reviewers/?techDocId=${techDocId}&versionId=${versionId}&memberId=${memberId}`, { method: "POST" })
            .then(() => onChanged && onChanged())
            .finally(() => { setBusy(null); setAdding(false); });
    };

    const removeReviewer = (memberId) => {
        if (!window.confirm("Remove this reviewer from the version?")) return;
        setBusy(memberId);
        fetch(`/umbraco/api/TechnicalDocumentation/Reviewers/?techDocId=${techDocId}&versionId=${versionId}&memberId=${memberId}`, { method: "DELETE" })
            .then(() => onChanged && onChanged())
            .finally(() => setBusy(null));
    };

    return (
        <div style={{ overflowY: "auto", paddingRight: 8, maxWidth: 620 }}>
            <div className="td-panel-h" style={{ marginTop: 0 }}>Reviewers</div>
            <p className="td-muted" style={{ fontSize: 12.5 }}>
                Any reviewer on this list may review sections, approve or reject them, and sign the
                whole document once every section is approved.
            </p>

            {reviewers.map((r) => (
                <div key={r.MemberId} className="td-rnode">
                    <Shared.Avatar name={r.Name} id={r.MemberId} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{r.Name || "—"}{r.MemberId == currentUserId ? " (you)" : ""}</div>
                        <div className="td-muted" style={{ fontSize: 11.5 }}>
                            Reviewer for v{currentVersion?.VersionNumber}{r.Role ? ` · ${r.Role}` : ""}
                        </div>
                    </div>
                    {canManage && (
                        <span className="td-btn-outline" onClick={() => busy !== r.MemberId && removeReviewer(r.MemberId)}>
                            {busy === r.MemberId ? "…" : (<><i className="fas fa-times" /> Remove</>)}
                        </span>
                    )}
                </div>
            ))}
            {reviewers.length === 0 && <div className="td-muted" style={{ fontSize: 12.5, margin: "8px 0" }}>No reviewers assigned yet.</div>}

            {canManage && (
                adding ? (
                    <select autoFocus defaultValue="" disabled={!!busy}
                        onChange={(e) => addReviewer(e.target.value)}
                        onBlur={() => setAdding(false)}
                        style={{ fontSize: 12.5, border: "1px solid #cbd5e0", borderRadius: 6, padding: "5px 8px", marginTop: 6 }}>
                        <option value="" disabled>Choose a reviewer…</option>
                        {availableUsers.map((u) => <option key={u.Id} value={u.Id}>{u.Name}</option>)}
                    </select>
                ) : (
                    <span className="td-tag td-tag-add" style={{ marginTop: 6 }} onClick={() => setAdding(true)}>+ Add reviewer</span>
                )
            )}

            <div className="td-hr" />
            <div className="td-rnode">
                <Shared.Avatar name={author?.Name} id={author?.Id} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{author?.Name || "—"}</div>
                    <div className="td-muted" style={{ fontSize: 11.5 }}>Author</div>
                </div>
                {canManage && (
                    editingAuthor ? (
                        <select autoFocus defaultValue={currentVersion?.Author || ""} disabled={savingAuthor}
                            onChange={(e) => changeAuthor(e.target.value)}
                            onBlur={() => setEditingAuthor(false)}
                            style={{ fontSize: 12, border: "1px solid #cbd5e0", borderRadius: 6, padding: "4px 8px" }}>
                            <option value="" disabled>— choose —</option>
                            {users.map((u) => <option key={u.Id} value={u.Id}>{u.Name}</option>)}
                        </select>
                    ) : (
                        <span className="td-btn-outline" onClick={() => setEditingAuthor(true)}>
                            <i className="fas fa-pen" /> Change
                        </span>
                    )
                )}
            </div>
        </div>
    );
};


const HistoryTab = ({ techDocId, versions, users }) => (
    <div style={{ overflowY: "auto", paddingRight: 8 }}>
        <div className="td-panel-h" style={{ marginTop: 0 }}>Versions</div>
        {versions.map((v) => (
            <div key={v.Id} className="td-kv">
                <span><b>v{v.VersionNumber}</b>{" "}<Shared.VersionStatePill published={v.Published} /></span>
                <span>
                    {v.Published
                        ? `Approved ${Shared.formatDate(v.UpdatedOn)}${v.ApprovedBy ? " · " + Shared.userName(users, v.ApprovedBy) : ""}`
                        : `Started ${Shared.formatDate(v.CreatedOn)}`}
                </span>
            </div>
        ))}
        <div className="td-hr" />
        <div className="td-panel-h">Activity</div>
        <Audit key={techDocId} EntityType="compliance-technical-documents" EntityId={techDocId} users={users}
            actionsUrl={`/umbraco/api/TechnicalDocumentation/Actions?techDocId=${techDocId}`} />
    </div>
);


const ConfirmPopup = ({ title, body, confirmLabel, busy, onConfirm, onClose }) => (
    <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="td-modal">
            <div className="td-modal-h">
                <h3>{title}</h3>
                <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
            </div>
            <div className="td-modal-b"><p style={{ fontSize: 13, lineHeight: 1.6, color: "#2d3748", margin: 0 }}>{body}</p></div>
            <div className="td-modal-f">
                <span />
                <div style={{ display: "flex", gap: 10 }}>
                    <button className="td-btn td-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="td-btn td-btn-primary" onClick={onConfirm} disabled={busy}>
                        {busy ? "Working…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    </div>
);


// Saves the version's current section titles/nesting (not content) as a reusable template --
// the same list AddItemPopup's "Create from template" dropdown draws from.
const SaveAsTemplateModal = ({ busy, error, isApproved, onSave, onClose }) => {
    const [name, setName] = useState("");
    return (
        <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="td-modal">
                <div className="td-modal-h">
                    <h3>Save as template</h3>
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
                <div className="td-modal-b">
                    {isApproved ? (<>
                        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#2d3748", margin: "0 0 12px" }}>
                            Saves this version's current section titles and nesting (not their content) as a
                            reusable template for creating new Technical Documentation.
                        </p>
                        <div className="td-field">
                            <label>TEMPLATE NAME</label>
                            <input
                                className="val" autoFocus value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && name.trim() && !busy) onSave(name); }}
                            />
                        </div>
                        {error && <div className="td-err" style={{ marginTop: 8 }}>{error}</div>}
                    </>) : (
                        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#2d3748", margin: 0 }}>
                            Only an approved version can be saved as a template. Review and sign this version first, then try again.
                        </p>
                    )}
                </div>
                <div className="td-modal-f">
                    <span />
                    <div style={{ display: "flex", gap: 10 }}>
                        <button className="td-btn td-btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
                        {isApproved && (
                            <button className="td-btn td-btn-primary" onClick={() => onSave(name)} disabled={busy || !name.trim()}>
                                {busy ? "Saving…" : "Save template"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


// Prompts for a section title -- opened from SectionTree's "+" actions (root or a specific parent).
const AddSectionModal = ({ busy, error, onSave, onClose }) => {
    const [title, setTitle] = useState("New section");
    return (
        <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="td-modal">
                <div className="td-modal-h">
                    <h3>Add section</h3>
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
                <div className="td-modal-b">
                    <div className="td-field">
                        <label>SECTION TITLE</label>
                        <input
                            className="val" autoFocus value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && title.trim() && !busy) onSave(title); }}
                        />
                    </div>
                    {error && <div className="td-err" style={{ marginTop: 8 }}>{error}</div>}
                </div>
                <div className="td-modal-f">
                    <span />
                    <div style={{ display: "flex", gap: 10 }}>
                        <button className="td-btn td-btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
                        <button className="td-btn td-btn-primary" onClick={() => onSave(title)} disabled={busy || !title.trim()}>
                            {busy ? "Adding…" : "Add section"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// Blocks navigating away from a section with unsaved edits. "Cancel" matches every other Cancel in
// this app (stay, don't proceed) -- the two ways to actually leave are explicit: Discard or Save.
const UnsavedChangesModal = ({ error, busy, onCancel, onDiscard, onSave }) => (
    <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
        <div className="td-modal">
            <div className="td-modal-h">
                <h3>Unsaved changes</h3>
                <span className="td-close-x" onClick={onCancel}><i className="fas fa-times" /></span>
            </div>
            <div className="td-modal-b">
                <p style={{ fontSize: 13, lineHeight: 1.6, color: "#2d3748", margin: 0 }}>
                    This section has unsaved changes. Save them, or discard and leave without saving.
                </p>
                {error && <div className="td-err" style={{ marginTop: 10 }}>{error}</div>}
            </div>
            <div className="td-modal-f">
                <button className="td-btn td-btn-outline" onClick={onDiscard} disabled={busy}>Discard</button>
                <div style={{ display: "flex", gap: 10 }}>
                    <button className="td-btn td-btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button className="td-btn td-btn-primary" onClick={onSave} disabled={busy}>
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    </div>
);


const EditDocumentModal = ({ techDoc, docJurisdictions, jurisdictionOptions, AppContext, onSave, onClose }) => {
    const [name, setName] = useState(techDoc?.Name || "");
    const [status, setStatus] = useState(techDoc?.Status ?? 0);
    const [format, setFormat] = useState(techDoc?.Format ?? 0);
    const [jurisdictionIds, setJurisdictionIds] = useState(docJurisdictions.map((j) => j.Id));
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const selected = jurisdictionOptions.filter((o) => jurisdictionIds.includes(o.Id));
    const available = jurisdictionOptions.filter((o) => !jurisdictionIds.includes(o.Id));

    const save = () => {
        if (!name.trim()) { setErr("Name is required."); return; }
        setBusy(true);
        setErr(null);
        onSave({ Name: name.trim(), Status: Number(status), Format: Number(format), JurisdictionIds: jurisdictionIds })
            .catch(() => { setErr("Could not save."); setBusy(false); });
    };

    return (
        <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="td-modal">
                <div className="td-modal-h">
                    <h3>Edit technical documentation</h3>
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
                <div className="td-modal-b">
                    <div className="td-field">
                        <label>Name</label>
                        <input className="val" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="td-field">
                        <label>Compliance status</label>
                        <select className="val" value={status} onChange={(e) => setStatus(e.target.value)}>
                            {ComplianceStatuses.filter((s) => s.id !== 6).map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="td-field">
                        <label>Format</label>
                        <select className="val" value={format} onChange={(e) => setFormat(e.target.value)}>
                            {Object.entries(FieldTypes.TD_FORMATS).map(([id, label]) => (
                                <option key={id} value={id}>{label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="td-field">
                        <label>Jurisdictions</label>
                        <div className="td-row" style={{ gap: 6, flexWrap: "wrap" }}>
                            {selected.map((j) => (
                                <span key={j.Id} className="td-tag">
                                    {j.Name}
                                    <span className="x" onClick={() => setJurisdictionIds(jurisdictionIds.filter((id) => id !== j.Id))}>
                                        <i className="fas fa-times-circle" />
                                    </span>
                                </span>
                            ))}
                            {adding ? (
                                <select autoFocus defaultValue=""
                                    onChange={(e) => { setJurisdictionIds([...jurisdictionIds, Number(e.target.value)]); setAdding(false); }}
                                    onBlur={() => setAdding(false)}
                                    style={{ fontSize: 11.5, borderRadius: 4, border: "1px solid #cbd5e0", padding: "3px 6px" }}>
                                    <option value="" disabled>Choose…</option>
                                    {available.map((o) => <option key={o.Id} value={o.Id}>{o.Name}</option>)}
                                </select>
                            ) : (
                                <span className="td-tag td-tag-add" onClick={() => setAdding(true)}>+ Add jurisdiction</span>
                            )}
                        </div>
                    </div>
                    {techDoc?.CustomFields?.length > 0 && (
                        <div className="td-field">
                            <label>Custom fields</label>
                            <FieldTypes.FieldType_CustomUserFieldsView globalContext={AppContext} isEditing={true} />
                        </div>
                    )}
                    {err && <div className="td-err">{err}</div>}
                </div>
                <div className="td-modal-f">
                    <span />
                    <div style={{ display: "flex", gap: 10 }}>
                        <button className="td-btn td-btn-cancel" onClick={onClose}>Cancel</button>
                        <button className="td-btn td-btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};


const SubmissionPicker = ({ onSend, onClose }) => {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedTitle, setSelectedTitle] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            setIsLoading(true);
            fetch(`/umbraco/api/Submission/Submissions/?search=${encodeURIComponent(search)}&sortBy=Id&sortType=DESCENDING&page=1&returnAll=true`)
                .then((r) => r.json())
                .then((d) => setResults(Array.isArray(d) ? d.slice(0, 30) : []))
                .catch(() => setResults([]))
                .finally(() => setIsLoading(false));
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const send = () => {
        if (!selectedId) return;
        setBusy(true);
        setErr(null);
        onSend(selectedId)
            .then(() => setSent(true))
            .catch(() => setErr("Could not send to that submission."))
            .finally(() => setBusy(false));
    };

    if (sent) {
        return (
            <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
                <div className="td-modal">
                    <div className="td-modal-h">
                        <h3><i className="fas fa-check-circle" /> Sent</h3>
                        <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                    </div>
                    <div className="td-modal-b">
                        <div className="td-receipt">Sent to <b>{selectedTitle}</b>.</div>
                    </div>
                    <div className="td-modal-f">
                        <span />
                        <button className="td-btn td-btn-primary" onClick={onClose}>Done</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="td-modal wide">
                <div className="td-modal-h">
                    <h3><i className="fas fa-share-square" /> Send to submission</h3>
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
                <div className="td-modal-b">
                    <p className="td-muted" style={{ fontSize: 12.5 }}>
                        Pick a submission to send this document to. Its section structure is recreated
                        as folders, each with that section's latest approved PDF.
                    </p>
                    <input
                        placeholder="Search submissions…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                        {isLoading ? (
                            <div className="td-center-loading" style={{ marginTop: 14 }}><LoadingSpinnerImage /></div>
                        ) : results.length === 0 ? (
                            <div className="td-muted" style={{ padding: 12, fontSize: 12 }}>No submissions found.</div>
                        ) : (
                            results.map((s) => (
                                <div
                                    key={s.Id}
                                    onClick={() => { setSelectedId(s.Id); setSelectedTitle(s.Title || `Submission #${s.Id}`); }}
                                    style={{
                                        padding: "8px 12px", fontSize: 12.5, cursor: "pointer",
                                        background: selectedId === s.Id ? "#ebf8ff" : "transparent",
                                        borderBottom: "1px solid #f0f4f8",
                                    }}
                                >
                                    {selectedId === s.Id && <i className="fas fa-check" style={{ marginRight: 8, color: "#2b6cb0" }} />}
                                    {s.Title || `Submission #${s.Id}`}
                                </div>
                            ))
                        )}
                    </div>
                    {err && <div className="td-err" style={{ marginTop: 10 }}>{err}</div>}
                </div>
                <div className="td-modal-f">
                    <span />
                    <div style={{ display: "flex", gap: 10 }}>
                        <button className="td-btn td-btn-cancel" onClick={onClose}>Cancel</button>
                        <button className="td-btn td-btn-primary" onClick={send} disabled={busy || !selectedId}>
                            {busy ? "Sending…" : "Send"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ItemDetailsPane;
